import { ContextualMenuButton } from 'app/design/icons/contextual-menu-button';
import { Column, Row } from 'app/design/primitives/box';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { useNavigation } from 'app/hooks/navigation';
import { localeAwareFormat, localeAwareFormatDistance, localeAwareFormatDuration } from 'app/lib/date';
import { LoanFilterKey, LoanType, useAllLoans, useDeleteRating, useReportRating } from 'app/network/loan';
import { navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import type { PageScrollViewRef } from 'app/design/primitives/page';
import type { Loan } from 'app/types/loan';
import type { Profile } from 'app/types/profile';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  FlatList,
  Pressable,
  ScrollView,
  View,
  useWindowDimensions,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  type ViewStyle,
} from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconAlertTriangle, IconPencil, IconTrash, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH, isMobileNative } from 'app/utils/constants';
import { getWebWindow } from 'app/utils/web-window';
const theme = createDSTheme();

type RatingItem = NonNullable<Profile['ratings']>[number];
type RatingFilter = 'all' | 'borrower' | 'lender' | 'positive' | 'negative';
const RATING_COMMENT_PREVIEW_LENGTH = 250;
const WEB_DRAG_THRESHOLD_PX = 4;

type WebDragScrollStyle = Omit<ViewStyle, 'cursor'> & {
  cursor?: 'auto' | 'pointer' | 'grab' | 'grabbing';
  userSelect?: 'auto' | 'none' | 'text' | 'contain' | 'all';
};

function useWebClickToDragScroll<ItemT>() {
  const scrollRef = useRef<FlatList<ItemT>>(null);
  const dragStateRef = useRef<{
    pointerId: number | null;
    startX: number;
    startScrollLeft: number;
  } | null>(null);
  const scrollLeftRef = useRef(0);
  const didDragRef = useRef(false);
  const [dragActive, setDragActive] = useState(false);

  const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    scrollLeftRef.current = event.nativeEvent.contentOffset.x;
  };

  const onMouseDown = (event: { button?: number; clientX: number; preventDefault?: () => void }) => {
    if (isMobileNative || !getWebWindow() || (event.button !== undefined && event.button !== 0)) {
      return;
    }

    didDragRef.current = false;
    dragStateRef.current = {
      pointerId: null,
      startX: event.clientX,
      startScrollLeft: scrollLeftRef.current,
    };
    setDragActive(true);
  };

  const window = getWebWindow();

  React.useEffect(() => {
    if (!dragActive || isMobileNative || !window || typeof document === 'undefined') return;

    const resetDragState = () => {
      dragStateRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    const endDrag = () => {
      resetDragState();
      setDragActive(false);
    };
    const onMouseMove = (moveEvent: MouseEvent) => {
      const state = dragStateRef.current;
      if (!state) {
        return;
      }
      const deltaX = moveEvent.clientX - state.startX;
      if (!didDragRef.current && Math.abs(deltaX) < WEB_DRAG_THRESHOLD_PX) {
        return;
      }
      if (!didDragRef.current) {
        didDragRef.current = true;
        document.body.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';
      }
      moveEvent.preventDefault();
      scrollRef.current?.scrollToOffset({ offset: state.startScrollLeft - deltaX, animated: false });
    };

    const onMouseUp = () => {
      endDrag();
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('blur', endDrag);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('blur', endDrag);
      resetDragState();
    };
  }, [dragActive, window]);

  const suppressChipPressIfDragged = () => {
    if (!didDragRef.current) {
      return false;
    }
    didDragRef.current = false;
    return true;
  };

  const webScrollStyle: WebDragScrollStyle | undefined = !isMobileNative
    ? {
        cursor: 'grab',
        userSelect: 'none',
      }
    : undefined;

  return { scrollRef, onScroll, onMouseDown, webScrollStyle, suppressChipPressIfDragged };
}

function getRatingKey(rating: RatingItem, index: number) {
  return `${rating.loanId}-rating-${String(index)}`;
}

function getBadgeStyle(score: number) {
  if (score >= 4) {
    return {
      backgroundColor: theme.colors['surface.success.subtle'],
      emoji: '👍',
    };
  }

  if (score === 3) {
    return {
      backgroundColor: theme.colors['surface.warning.subtle'],
      emoji: '😐',
    };
  }

  return {
    backgroundColor: theme.colors['surface.error.subtle'],
    emoji: '😬',
  };
}

function toDurationLabel(start?: string, end?: string) {
  if (!start || !end) {
    return null;
  }

  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return null;
  }

  const seconds = Math.max(0, Math.floor(Math.abs(endDate.getTime() - startDate.getTime()) / 1000));
  return localeAwareFormatDuration(seconds, true, true);
}

function ratingTagItems(t: ReturnType<typeof useTranslation>['t'], rating: RatingItem) {
  const tags: { key: string; label: string; value: number }[] = [];

  if (typeof rating.assetQualityRating === 'number') {
    tags.push({
      key: 'assetQuality',
      label: t('reviews.labels.assetQuality', { ns: 'neighbour-profile' }),
      value: rating.assetQualityRating,
    });
  }

  if (typeof rating.logisticsReliabilityRating === 'number') {
    tags.push({
      key: 'logisticsReliability',
      label: t('reviews.labels.logisticsReliability', { ns: 'neighbour-profile' }),
      value: rating.logisticsReliabilityRating,
    });
  }
  if (typeof rating.communityTrustRating === 'number') {
    tags.push({
      key: 'communityTrust',
      label: t('reviews.labels.communityTrust', { ns: 'neighbour-profile' }),
      value: rating.communityTrustRating,
    });
  }

  return tags;
}

function getLoanSortTime(loan: Loan) {
  return new Date(loan.returned ?? loan.end).getTime();
}

function getUserRoleForLoan(loan: Loan, currentUserId?: string) {
  if (!currentUserId) {
    return null;
  }

  if (loan.lenderId === currentUserId) {
    return 'lender' as const;
  }

  if (loan.borrowerId === currentUserId) {
    return 'borrower' as const;
  }

  return null;
}

function hasCurrentUserRatedLoan(loan: Loan, currentUserId?: string) {
  const role = getUserRoleForLoan(loan, currentUserId);

  if (role === 'lender') {
    return typeof loan.rating?.lenderRating === 'number';
  }

  if (role === 'borrower') {
    return typeof loan.rating?.borrowerRating === 'number';
  }

  return true;
}

function PendingRatingPrompt({
  loan,
  currentUserId,
  onDismiss,
  style,
}: {
  loan: Loan;
  currentUserId?: string;
  onDismiss: () => void;
  style?: ViewStyle;
}) {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const currentUserRole = getUserRoleForLoan(loan, currentUserId);
  const isLender = currentUserRole === 'lender';
  const otherUser = isLender ? loan.borrower : loan.lender;
  const otherUserName = [otherUser.profile?.firstName, otherUser.profile?.lastName].filter(Boolean).join(' ').trim();
  const listingImage = loan.listing.picture ?? loan.listing.pictures?.[0]?.url ?? null;
  const subjectImage = isLender ? (otherUser.profile?.picture ?? null) : listingImage;
  const subjectName = isLender
    ? otherUserName || t('reviews.unknownUser', { ns: 'neighbour-profile' })
    : loan.listing.item.name;
  const dateRange = `${localeAwareFormat(new Date(loan.start), 'd MMM')} ${t('reviews.pendingPrompt.dateTo', {
    ns: 'neighbour-profile',
  })} ${localeAwareFormat(new Date(loan.returned ?? loan.end), 'd MMM')}`;

  const handlePress = () => {
    navigation.navigate(
      'new-rating',
      {
        loanId: loan.id,
        listingId: loan.listingId,
        channelUrl: loan.channelURL,
        isLender,
        subjectImage,
        subjectName,
      },
      { pop: true },
    );
  };

  return (
    <Column
      alignItems="center"
      gap="spacing.general.x2"
      style={[
        {
          backgroundColor: theme.colors['surface.default'],
          borderColor: theme.colors['surface.disabled'],
          borderRadius: 16,
          borderWidth: 1,
          padding: theme.spacing['spacing.general.x4'],
        },
        style,
      ]}
    >
      <Pressable
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        onPress={onDismiss}
        style={{ position: 'absolute', right: 9, top: 8, zIndex: 1 }}
      >
        <IconX strokeWidth={ICON_STROKE_WIDTH} size={30} color={theme.colors['text.primary']} />
      </Pressable>

      <Column alignItems="center" gap="spacing.general.x2" style={{ paddingTop: theme.spacing['spacing.general.x4'] }}>
        <Column
          alignItems="center"
          backgroundColor={listingImage ? 'surface.tertiary-neutral' : 'surface.brand.secondary-5.subtle'}
          height={48}
          justifyContent="center"
          style={{ borderRadius: 12, overflow: 'hidden' }}
          width={48}
        >
          {listingImage ? (
            <PCImage
              cachePolicy="memory-disk"
              contentFit="cover"
              height={48}
              source={{ uri: listingImage }}
              width={48}
            />
          ) : (
            <Text fontSize={24}>✋</Text>
          )}
        </Column>

        <Text fontSize={14} fontWeight="600" lineHeight={16} textAlign="center">
          {t('reviews.pendingPrompt.title', { ns: 'neighbour-profile' })}
        </Text>

        <Text color="text.secondary" fontSize={14} lineHeight={16} textAlign="center">
          {t(isLender ? 'reviews.pendingPrompt.bodyLendingStart' : 'reviews.pendingPrompt.bodyBorrowingStart', {
            ns: 'neighbour-profile',
          })}
          <Text color="text.secondary" fontSize={14} fontWeight="700" lineHeight={16}>
            {loan.listing.item.name}
          </Text>
          {t('reviews.pendingPrompt.bodyWith', {
            ns: 'neighbour-profile',
            name: otherUserName || t('reviews.unknownUser', { ns: 'neighbour-profile' }),
          })}
          <Text color="text.secondary" fontSize={14} fontWeight="700" lineHeight={16}>
            {dateRange}
          </Text>
        </Text>
      </Column>

      <Pressable
        onPress={handlePress}
        style={{
          alignItems: 'center',
          borderColor: theme.colors['text.primary'],
          borderRadius: 24,
          borderWidth: 1,
          height: 48,
          justifyContent: 'center',
          marginTop: theme.spacing['spacing.general.x2'],
          width: '100%',
        }}
      >
        <Text fontSize={16} fontWeight="700" lineHeight={20}>
          {t('reviews.pendingPrompt.button', { ns: 'neighbour-profile' })}
        </Text>
      </Pressable>
    </Column>
  );
}

function getRatingCardViewModel(
  rating: RatingItem,
  t: ReturnType<typeof useTranslation>['t'],
  expandedComment: boolean,
  currentUserId?: string,
) {
  const overallRating = rating.overallRating ?? null;
  const isRecommended = overallRating !== null ? overallRating >= 3 : false;
  const comment = rating.comment?.trim();
  const shouldCollapseComment = Boolean(comment && comment.length > RATING_COMMENT_PREVIEW_LENGTH);

  return {
    ago: rating.createdAt ? localeAwareFormatDistance(new Date(), new Date(rating.createdAt)) : null,
    duration: toDurationLabel(rating.start, rating.end),
    isOwnRating: Boolean(currentUserId && rating.reviewerUserId && currentUserId === rating.reviewerUserId),
    recommendationColor: isRecommended ? theme.colors['text.success'] : theme.colors['icon.error'],
    recommendationEmoji: isRecommended ? '👍' : '👎',
    recommendationText: isRecommended
      ? t('reviews.recommended', { ns: 'neighbour-profile' })
      : t('reviews.notRecommended', { ns: 'neighbour-profile' }),
    reviewerName: [rating.reviewerFirstName, rating.reviewerLastName].filter(Boolean).join(' ').trim(),
    roleLabel:
      rating.reviewerRole === 'borrower'
        ? t('reviews.role.borrower', { ns: 'neighbour-profile' })
        : t('reviews.role.lender', { ns: 'neighbour-profile' }),
    shouldCollapseComment,
    tags: ratingTagItems(t, rating),
    visibleComment:
      shouldCollapseComment && !expandedComment
        ? `${comment!.slice(0, RATING_COMMENT_PREVIEW_LENGTH).trimEnd()}...`
        : comment,
  };
}

interface RatingCardHeaderProps {
  ago: string | null;
  duration: string | null;
  loanLabel: string;
  onOpenMenu: () => void;
  onPressReviewer: () => void;
  rating: RatingItem;
  recommendationColor: string;
  recommendationEmoji: string;
  recommendationText: string;
  reviewerName: string;
  roleLabel: string;
  showMenu: boolean;
  unknownUserLabel: string;
}

function RatingCardHeader({
  ago,
  duration,
  loanLabel,
  onOpenMenu,
  onPressReviewer,
  rating,
  recommendationColor,
  recommendationEmoji,
  recommendationText,
  reviewerName,
  roleLabel,
  showMenu,
  unknownUserLabel,
}: RatingCardHeaderProps) {
  return (
    <Column>
      <Row alignItems="center" justifyContent="space-between">
        <Pressable disabled={!rating.reviewerUserId} onPress={onPressReviewer} style={{ flex: 1 }}>
          <Row alignItems="center" gap="spacing.general.x2">
            {rating.reviewerPicture ? (
              <PCImage
                cachePolicy="memory-disk"
                contentFit="cover"
                height={40}
                source={{ uri: rating.reviewerPicture }}
                style={{ borderRadius: 20 }}
                width={40}
              />
            ) : (
              <Column
                alignItems="center"
                backgroundColor="surface.tertiary-neutral"
                height={40}
                justifyContent="center"
                style={{ borderRadius: 20 }}
                width={40}
              >
                <Text fontWeight="700" variant="bodyBase">
                  {(reviewerName || '?').slice(0, 1).toUpperCase()}
                </Text>
              </Column>
            )}
            <Column flex={1}>
              <Text numberOfLines={1} variant="bodySmallMedium">
                {reviewerName || unknownUserLabel}
              </Text>
              <Text color="text.secondary" fontSize={12} numberOfLines={1}>
                {roleLabel}
              </Text>
            </Column>
          </Row>
        </Pressable>
        {showMenu ? (
          <Pressable hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} onPress={onOpenMenu} style={{ padding: 4 }}>
            <ContextualMenuButton style={{ transform: [{ rotate: '90deg' }] }} />
          </Pressable>
        ) : null}
      </Row>

      <Row alignItems="baseline" marginTop="spacing.general.x2" flexWrap="wrap" gap="spacing.general.x1">
        <Text style={{ color: recommendationColor }} variant="bodySmallMedium">
          {`${recommendationEmoji} ${recommendationText}`}
        </Text>
        {ago ? (
          <Text color="text.secondary" fontSize={12}>
            {`· ${ago}`}
          </Text>
        ) : null}
        {duration ? (
          <Text color="text.secondary" fontSize={12}>
            {`· ${duration} ${loanLabel}`}
          </Text>
        ) : null}
      </Row>
    </Column>
  );
}

function RatingTags({ rating, tags }: { rating: RatingItem; tags: ReturnType<typeof ratingTagItems> }) {
  if (tags.length === 0) return null;

  return (
    <Row flexWrap="wrap" rowGap="spacing.general.x2" columnGap="spacing.general.x2" marginTop="spacing.general.x2">
      {tags.map((tag) => {
        const badge = getBadgeStyle(tag.value);
        return (
          <View
            key={`${rating.loanId}-${tag.key}`}
            style={{
              backgroundColor: badge.backgroundColor,
              borderRadius: 1000,
              paddingHorizontal: theme.spacing['spacing.general.x2'],
              paddingVertical: theme.spacing['spacing.general.x1'],
            }}
          >
            <Text fontWeight="700" style={{ color: theme.colors['text.primary'] }} variant="bodyXSmallMedium">
              {`${badge.emoji} ${tag.label}`}
            </Text>
          </View>
        );
      })}
    </Row>
  );
}

interface RatingDetailsProps {
  expandedComment: boolean;
  hideListing?: boolean;
  listingFallbackLabel: string;
  listingOfferLabel: string;
  listingRequestLabel: string;
  onPressListing?: (listingId: string) => void;
  onToggleComment: () => void;
  rating: RatingItem;
  readLessLabel: string;
  readMoreLabel: string;
  shouldCollapseComment: boolean;
  visibleComment?: string;
}

function RatingComment({
  expandedComment,
  onToggleComment,
  readLessLabel,
  readMoreLabel,
  shouldCollapseComment,
  visibleComment,
}: Pick<
  RatingDetailsProps,
  'expandedComment' | 'onToggleComment' | 'readLessLabel' | 'readMoreLabel' | 'shouldCollapseComment' | 'visibleComment'
>) {
  if (!visibleComment) return null;

  return (
    <Pressable disabled={!shouldCollapseComment} onPress={onToggleComment}>
      <Text color="text.primary" fontSize={16} lineHeight={22.4}>
        {visibleComment}
        {shouldCollapseComment ? (
          <>
            <Text> </Text>
            <Text
              color="text.interactive"
              fontSize={14}
              fontWeight="700"
              lineHeight={22.4}
              style={{ textDecorationLine: 'underline' }}
            >
              {expandedComment ? readLessLabel : readMoreLabel}
            </Text>
          </>
        ) : null}
      </Text>
    </Pressable>
  );
}

function RatingListing({
  hideListing,
  listingFallbackLabel,
  listingOfferLabel,
  listingRequestLabel,
  onPressListing,
  rating,
}: Pick<
  RatingDetailsProps,
  'hideListing' | 'listingFallbackLabel' | 'listingOfferLabel' | 'listingRequestLabel' | 'onPressListing' | 'rating'
>) {
  const listingId = rating.listingId;
  if (!listingId || hideListing) return null;

  return (
    <Pressable
      onPress={() => onPressListing?.(listingId)}
      style={{
        backgroundColor: theme.colors['surface.primary-neutral'],
        borderRadius: theme.borderRadii['radius.md'],
        padding: theme.spacing['spacing.general.x1'],
        paddingRight: theme.spacing['spacing.general.x2'],
      }}
    >
      <Row alignItems="center" gap="spacing.general.x2">
        <Column
          alignItems="center"
          backgroundColor={rating.listingPicture ? 'surface.tertiary-neutral' : 'surface.brand.secondary-5.subtle'}
          height={32}
          justifyContent="center"
          style={{ borderRadius: 6, overflow: 'hidden' }}
          width={32}
        >
          {rating.listingPicture ? (
            <PCImage
              cachePolicy="memory-disk"
              contentFit="cover"
              height={32}
              source={{ uri: rating.listingPicture }}
              width={32}
            />
          ) : (
            <Text>✋</Text>
          )}
        </Column>

        <Column flex={1}>
          <Text color="text.secondary" fontSize={11}>
            {rating.listingType === 'request' ? listingRequestLabel : listingOfferLabel}
          </Text>
          <Text numberOfLines={1} variant="bodyXSmallMedium">
            {rating.listingTitle || listingFallbackLabel}
          </Text>
        </Column>
        <Text color="text.secondary" fontSize={20}>
          ›
        </Text>
      </Row>
    </Pressable>
  );
}

function RatingDetails({
  expandedComment,
  hideListing,
  listingFallbackLabel,
  listingOfferLabel,
  listingRequestLabel,
  onPressListing,
  onToggleComment,
  rating,
  readLessLabel,
  readMoreLabel,
  shouldCollapseComment,
  visibleComment,
}: RatingDetailsProps) {
  const listingId = rating.listingId;
  const showListing = Boolean(listingId && !hideListing);
  if (!visibleComment && !showListing) return null;

  return (
    <Column gap="spacing.general.x5" marginTop="spacing.general.x5">
      <RatingComment
        expandedComment={expandedComment}
        onToggleComment={onToggleComment}
        readLessLabel={readLessLabel}
        readMoreLabel={readMoreLabel}
        shouldCollapseComment={shouldCollapseComment}
        visibleComment={visibleComment}
      />
      <RatingListing
        hideListing={hideListing}
        listingFallbackLabel={listingFallbackLabel}
        listingOfferLabel={listingOfferLabel}
        listingRequestLabel={listingRequestLabel}
        onPressListing={onPressListing}
        rating={rating}
      />
    </Column>
  );
}

function RatingCardMenu({
  deleteLabel,
  editLabel,
  isOpen,
  isOwnRating,
  onClose,
  onDeleteRating,
  onEditRating,
  onReportRating,
  reportLabel,
}: {
  deleteLabel: string;
  editLabel: string;
  isOpen: boolean;
  isOwnRating: boolean;
  onClose: () => void;
  onDeleteRating?: () => void;
  onEditRating?: () => void;
  onReportRating?: () => void;
  reportLabel: string;
}) {
  const runAndClose = (action: () => void) => {
    onClose();
    action();
  };

  return (
    <BottomSheet onClose={onClose} open={isOpen}>
      {isOwnRating ? (
        <>
          {onEditRating ? (
            <BottomSheet.Action
              onPress={() => runAndClose(onEditRating)}
              prefix={<IconPencil strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />}
            >
              <Text variant="bodyBase">{editLabel}</Text>
            </BottomSheet.Action>
          ) : null}
          {onDeleteRating ? (
            <BottomSheet.Action
              onPress={() => runAndClose(onDeleteRating)}
              prefix={<IconTrash strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['surface.error.default']} />}
            >
              <Text color="surface.error.default" variant="bodyBase">
                {deleteLabel}
              </Text>
            </BottomSheet.Action>
          ) : null}
        </>
      ) : onReportRating ? (
        <BottomSheet.Action
          onPress={() => runAndClose(onReportRating)}
          prefix={<IconAlertTriangle strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['surface.error.default']} />}
        >
          <Text color="surface.error.default" variant="bodyBase">
            {reportLabel}
          </Text>
        </BottomSheet.Action>
      ) : null}
    </BottomSheet>
  );
}

function useRatingFilter(showRoleFilters: boolean) {
  const [activeFilter, setActiveFilter] = useState<RatingFilter>('all');
  const roleFilterIsActive = activeFilter === 'borrower' || activeFilter === 'lender';

  if (!showRoleFilters && roleFilterIsActive) {
    setActiveFilter('all');
  }

  return { activeFilter, setActiveFilter };
}

export function RatingCard({
  rating,
  onPressListing,
  forcedWidth,
  hideListing,
  currentUserId,
  onEditRating,
  onDeleteRating,
  onReportRating,
  hideTopLine,
  compact = false,
}: {
  rating: RatingItem;
  onPressListing?: (listingId: string) => void;
  forcedWidth?: ViewStyle['width'];
  hideListing?: boolean;
  currentUserId?: string;
  onEditRating?: () => void;
  onDeleteRating?: () => void;
  onReportRating?: () => void;
  hideTopLine?: boolean;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedComment, setExpandedComment] = useState(false);

  const viewModel = getRatingCardViewModel(rating, t, expandedComment, currentUserId);
  const showMenu = Boolean(currentUserId && (onEditRating || onDeleteRating || onReportRating));

  return (
    <Column
      borderColor="surface.disabled"
      borderTopWidth={hideTopLine ? 0 : 1}
      paddingBottom={compact ? 'spacing.general.x1' : 'spacing.general.x8'}
      paddingTop={compact ? 'spacing.general.x3' : 'spacing.general.x8'}
      gap="spacing.general.x1"
      style={forcedWidth !== undefined ? { width: forcedWidth } : undefined}
    >
      <RatingCardHeader
        ago={viewModel.ago}
        duration={viewModel.duration}
        loanLabel={t('reviews.loanLabel', { ns: 'neighbour-profile' })}
        onOpenMenu={() => setIsMenuOpen(true)}
        onPressReviewer={() => {
          if (rating.reviewerUserId) navigateToUserProfile(navigation, rating.reviewerUserId);
        }}
        rating={rating}
        recommendationColor={viewModel.recommendationColor}
        recommendationEmoji={viewModel.recommendationEmoji}
        recommendationText={viewModel.recommendationText}
        reviewerName={viewModel.reviewerName}
        roleLabel={viewModel.roleLabel}
        showMenu={showMenu}
        unknownUserLabel={t('reviews.unknownUser', { ns: 'neighbour-profile' })}
      />
      <RatingTags rating={rating} tags={viewModel.tags} />
      <RatingDetails
        expandedComment={expandedComment}
        hideListing={hideListing}
        listingFallbackLabel={t('reviews.listingFallback', { ns: 'neighbour-profile' })}
        listingOfferLabel={t('reviews.listingType.offer', { ns: 'neighbour-profile' })}
        listingRequestLabel={t('reviews.listingType.request', { ns: 'neighbour-profile' })}
        onPressListing={onPressListing}
        onToggleComment={() => setExpandedComment((current) => !current)}
        rating={rating}
        readLessLabel={t('accordion:showLess')}
        readMoreLabel={t('posts:reviews.readMore', { ns: 'neighbour-profile' })}
        shouldCollapseComment={viewModel.shouldCollapseComment}
        visibleComment={viewModel.visibleComment}
      />
      <RatingCardMenu
        deleteLabel={t('reviews.actions.delete', { ns: 'neighbour-profile' })}
        editLabel={t('reviews.actions.edit', { ns: 'neighbour-profile' })}
        isOpen={isMenuOpen}
        isOwnRating={viewModel.isOwnRating}
        onClose={() => setIsMenuOpen(false)}
        onDeleteRating={onDeleteRating}
        onEditRating={onEditRating}
        onReportRating={onReportRating}
        reportLabel={t('reviews.actions.report', { ns: 'neighbour-profile' })}
      />
    </Column>
  );
}

interface RatingsSectionProps {
  ratings: NonNullable<Profile['ratings']>;
  subjectName: string;
  onPressListing?: (listingId: string) => void;
  currentUserId?: string;
  ratedUserId?: string;
  ratedListingId?: string;
  onEditRating?: (rating: RatingItem) => void;
  onNavigateToSupport?: () => void;
  scrollToLoanId?: string;
  parentScrollRef?: React.RefObject<PageScrollViewRef | null>;
  hideListing?: boolean;
  withPadding?: boolean;
  showPendingRatings?: boolean;
  hideSummary?: boolean;
  variant?: {
    listing?: 'visible' | 'hidden';
    padding?: 'with-padding' | 'without-padding';
    pendingRatings?: 'visible' | 'hidden';
    summary?: 'visible' | 'hidden';
  };
}

function useRatingsSectionModel({
  ratings,
  subjectName,
  onPressListing,
  currentUserId,
  ratedUserId,
  ratedListingId,
  onEditRating,
  onNavigateToSupport,
  scrollToLoanId,
  parentScrollRef,
  variant,
  hideListing: legacyHideListing,
  withPadding: legacyWithPadding,
  showPendingRatings: legacyShowPendingRatings,
  hideSummary: legacyHideSummary,
}: RatingsSectionProps) {
  const { t } = useTranslation();
  const { width: windowWidth } = useWindowDimensions();
  const { hideListing, showPendingRatings, withPadding, hideSummary } = getRatingsSectionDisplayOptions(
    variant,
    legacyHideListing,
    legacyWithPadding,
    legacyShowPendingRatings,
    legacyHideSummary,
  );
  const showRoleFilters = !hideListing && !ratedListingId;
  const { activeFilter, setActiveFilter } = useRatingFilter(showRoleFilters);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [dismissedPendingPromptIds, setDismissedPendingPromptIds] = useState<string[]>([]);
  const {
    scrollRef: filtersScrollRef,
    onScroll: onFiltersScroll,
    onMouseDown: onFiltersMouseDown,
    webScrollStyle,
    suppressChipPressIfDragged,
  } = useWebClickToDragScroll<{ key: RatingFilter; count: number }>();
  const { mutate: deleteRating } = useDeleteRating();
  const { mutate: reportRating } = useReportRating({
    onSuccess: () => {
      onNavigateToSupport?.();
    },
  });
  const shouldLoadPendingRatingLoans = Boolean(currentUserId) && showPendingRatings;
  const pastBorrowingLoansQuery = useAllLoans(LoanFilterKey.PastLoans, LoanType.Borrowing, {
    enabled: shouldLoadPendingRatingLoans,
  });
  const pastLendingLoansQuery = useAllLoans(LoanFilterKey.PastLoans, LoanType.Lending, {
    enabled: shouldLoadPendingRatingLoans,
  });

  const ratingSectionOffsetYRef = React.useRef(0);
  const ratingListOffsetYRef = React.useRef(0);
  const ratingCardOffsetYRef = React.useRef<Record<string, number>>({});
  const [ratingCardLayoutVersion, setRatingCardLayoutVersion] = useState(0);

  const filteredRatings = (() => {
    if (activeFilter === 'all') {
      return ratings;
    }

    return ratings.filter((rating) => {
      const overallRating = rating.overallRating;
      if (activeFilter === 'positive') {
        return (overallRating ?? 0) >= 3;
      }
      if (activeFilter === 'negative') {
        return (overallRating ?? 0) < 3;
      }
      return rating.reviewerRole === activeFilter;
    });
  })();

  const indexToScrollTo = filteredRatings.findIndex((rating) => rating.loanId === scrollToLoanId);

  React.useEffect(() => {
    if (!scrollToLoanId || indexToScrollTo === -1) {
      return;
    }

    const offsetY = ratingCardOffsetYRef.current[scrollToLoanId];
    if (typeof offsetY !== 'number') {
      return;
    }

    const targetY = Math.max(0, ratingSectionOffsetYRef.current + ratingListOffsetYRef.current + offsetY - 100);
    const timeout = setTimeout(() => {
      const parentScroll = parentScrollRef?.current;
      if (parentScroll?.scrollTo) {
        parentScroll.scrollTo({ x: 0, y: targetY, animated: true });
        return;
      }

      parentScroll?.scrollToPosition?.(0, targetY, true);
    }, 100);

    return () => clearTimeout(timeout);
  }, [indexToScrollTo, parentScrollRef, ratingCardLayoutVersion, scrollToLoanId]);

  const counts = {
    all: ratings.length,
    borrower: ratings.filter((rating) => rating.reviewerRole === 'borrower').length,
    lender: ratings.filter((rating) => rating.reviewerRole === 'lender').length,
    positive: ratings.filter((rating) => (rating.overallRating ?? 0) >= 3).length,
    negative: ratings.filter((rating) => (rating.overallRating ?? 0) < 3).length,
  };
  const ratingFilters = [
    { key: 'all' as const, count: counts.all },
    { key: 'positive' as const, count: counts.positive },
    { key: 'negative' as const, count: counts.negative },
    ...(showRoleFilters
      ? ([
          { key: 'borrower' as const, count: counts.borrower },
          { key: 'lender' as const, count: counts.lender },
        ] as const)
      : []),
  ] satisfies { key: RatingFilter; count: number }[];

  const topTags = (() => {
    const acc = new Map<string, { label: string; scoreSum: number; count: number }>();
    ratings.forEach((rating) => {
      ratingTagItems(t, rating).forEach((tag) => {
        const previous = acc.get(tag.key) ?? { label: tag.label, scoreSum: 0, count: 0 };
        acc.set(tag.key, {
          label: tag.label,
          scoreSum: previous.scoreSum + tag.value,
          count: previous.count + 1,
        });
      });
    });

    return [...acc.values()]
      .map((item) => ({ ...item, average: item.scoreSum / item.count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);
  })();

  const showOverAllTags =
    ratings.filter((rating) => rating.communityTrustRating !== null && rating.communityTrustRating !== undefined)
      .length >= 3; // Only show overall tags if we have at least 3 ratings with the star rating system, to ensure some level of reliability of the displayed average ratings for each tag

  const completedLoans = (() => {
    const merged = [...(pastBorrowingLoansQuery.data ?? []), ...(pastLendingLoansQuery.data ?? [])];
    const byId = new Map<string, Loan>();

    merged.forEach((loan) => {
      byId.set(loan.id, loan);
    });

    return [...byId.values()];
  })();

  const pendingRatingLoans = (() => {
    if (!showPendingRatings) {
      return [];
    }
    const shouldFilterByRatedUser = Boolean(ratedUserId && currentUserId && ratedUserId !== currentUserId);

    return completedLoans
      .filter((loan) => {
        const role = getUserRoleForLoan(loan, currentUserId);
        if (!role || hasCurrentUserRatedLoan(loan, currentUserId)) {
          return false;
        }

        if (ratedListingId && loan.listingId !== ratedListingId) {
          return false;
        }

        if (!shouldFilterByRatedUser) {
          return true;
        }

        return loan.borrowerId === ratedUserId || loan.lenderId === ratedUserId;
      })
      .sort((a, b) => getLoanSortTime(b) - getLoanSortTime(a));
  })();

  const dismissedPendingPromptIdSet = new Set(dismissedPendingPromptIds);
  const visiblePendingRatingLoans = pendingRatingLoans.filter((loan) => !dismissedPendingPromptIdSet.has(loan.id));
  const pendingPromptWidth = Math.max(280, Math.min(343, windowWidth - theme.spacing['spacing.general.x5'] * 2));
  const dismissPendingPrompt = (loanId: string) => {
    setDismissedPendingPromptIds((current) => (current.includes(loanId) ? current : [...current, loanId]));
  };

  const firstVisiblePendingRatingLoan = visiblePendingRatingLoans[0];
  const pendingPrompt =
    visiblePendingRatingLoans.length === 1 && firstVisiblePendingRatingLoan ? (
      <PendingRatingPrompt
        loan={firstVisiblePendingRatingLoan}
        currentUserId={currentUserId}
        onDismiss={() => dismissPendingPrompt(firstVisiblePendingRatingLoan.id)}
      />
    ) : visiblePendingRatingLoans.length > 1 ? (
      <FlatList
        data={visiblePendingRatingLoans}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          gap: theme.spacing['spacing.general.x2'],
          paddingHorizontal: theme.spacing['spacing.general.x5'],
        }}
        style={{
          marginHorizontal: -theme.spacing['spacing.general.x5'],
          marginBottom: theme.spacing['spacing.general.x4'],
        }}
        keyExtractor={(loan) => loan.id}
        renderItem={({ item: loan }) => (
          <PendingRatingPrompt
            loan={loan}
            currentUserId={currentUserId}
            onDismiss={() => dismissPendingPrompt(loan.id)}
            style={{ width: pendingPromptWidth }}
          />
        )}
      />
    ) : null;

  const handleDeleteRating = (rating: RatingItem) => {
    if (!rating.loanId) {
      return;
    }

    Alert.alert(
      t('reviews.actions.deleteConfirmTitle', { ns: 'neighbour-profile' }),
      t('reviews.actions.deleteConfirmMessage', { ns: 'neighbour-profile' }),
      [
        { text: t('reviews.actions.deleteConfirmCancel', { ns: 'neighbour-profile' }), style: 'cancel' },
        {
          text: t('reviews.actions.deleteConfirmOk', { ns: 'neighbour-profile' }),
          style: 'destructive',
          onPress: () => {
            if (!rating.loanId) {
              return;
            }

            deleteRating(rating.loanId);
          },
        },
      ],
    );
  };
  const paddingHorizontal = withPadding ? ('spacing.general.x5' as const) : undefined;
  const paddingHorizontalInPx = withPadding ? theme.spacing['spacing.general.x5'] : theme.spacing['spacing.general.x4'];

  const handleReportRating = (rating: RatingItem) => {
    if (!rating.loanId) {
      return;
    }

    reportRating(rating.loanId);
  };

  return {
    activeFilter,
    counts,
    currentUserId,
    filteredRatings,
    filtersScrollRef,
    handleDeleteRating,
    handleReportRating,
    hideListing,
    hideSummary,
    isInfoOpen,
    onEditRating,
    onFiltersMouseDown,
    onFiltersScroll,
    onPressListing,
    paddingHorizontal,
    paddingHorizontalInPx,
    pendingPrompt,
    ratingCardOffsetYRef,
    ratingFilters,
    ratingListOffsetYRef,
    ratingSectionOffsetYRef,
    ratings,
    setActiveFilter,
    setIsInfoOpen,
    setRatingCardLayoutVersion,
    showOverAllTags,
    subjectName,
    suppressChipPressIfDragged,
    t,
    topTags,
    webScrollStyle,
  };
}

function getRatingsSectionDisplayOptions(
  variant:
    | {
        listing?: 'visible' | 'hidden';
        padding?: 'with-padding' | 'without-padding';
        pendingRatings?: 'visible' | 'hidden';
        summary?: 'visible' | 'hidden';
      }
    | undefined,
  legacyHideListing: boolean | undefined,
  legacyWithPadding: boolean | undefined,
  legacyShowPendingRatings: boolean | undefined,
  legacyHideSummary: boolean | undefined,
) {
  const hideListing = variant?.listing ? variant.listing === 'hidden' : (legacyHideListing ?? false);
  const withPadding = variant?.padding ? variant.padding !== 'without-padding' : (legacyWithPadding ?? true);
  const showPendingRatings = variant?.pendingRatings
    ? variant.pendingRatings !== 'hidden'
    : (legacyShowPendingRatings ?? true);
  const hideSummary = variant?.summary ? variant.summary === 'hidden' : (legacyHideSummary ?? false);
  return { hideListing, showPendingRatings, withPadding, hideSummary };
}

function RatingsSectionView({
  model: {
    activeFilter,
    counts,
    currentUserId,
    filteredRatings,
    filtersScrollRef,
    handleDeleteRating,
    handleReportRating,
    hideListing,
    hideSummary,
    isInfoOpen,
    onEditRating,
    onFiltersMouseDown,
    onFiltersScroll,
    onPressListing,
    paddingHorizontal,
    paddingHorizontalInPx,
    pendingPrompt,
    ratingCardOffsetYRef,
    ratingFilters,
    ratingListOffsetYRef,
    ratingSectionOffsetYRef,
    ratings,
    setActiveFilter,
    setIsInfoOpen,
    setRatingCardLayoutVersion,
    showOverAllTags,
    subjectName,
    suppressChipPressIfDragged,
    t,
    topTags,
    webScrollStyle,
  },
}: {
  model: ReturnType<typeof useRatingsSectionModel>;
}) {
  if (ratings.length === 0) {
    return (
      <Column paddingHorizontal={paddingHorizontal} paddingTop="spacing.general.x4">
        {pendingPrompt}
        <Column alignItems="center" paddingTop="spacing.general.x5">
          <Column
            alignItems="center"
            justifyContent="center"
            style={{
              width: 64,
              height: 64,
              borderRadius: 32,
              backgroundColor: theme.colors['surface.primary-neutral'],
            }}
          >
            <Text fontSize={32} style={{ color: theme.colors['text.emphasized'] }}>
              👍
            </Text>
          </Column>
          <Text fontWeight="700" marginTop="spacing.general.x4" textAlign="center" variant="bodyBase">
            {t('reviews.empty.title', { ns: 'neighbour-profile', name: subjectName })}
          </Text>
          <Text color="text.tertiary" marginTop="spacing.general.x1" textAlign="center" variant="bodyBase">
            {t('reviews.empty.subtitle', { ns: 'neighbour-profile' })}
          </Text>
        </Column>
      </Column>
    );
  }

  return (
    <Column
      gap="spacing.general.x6"
      paddingHorizontal={paddingHorizontal}
      paddingTop="spacing.general.x4"
      onLayout={(event) => {
        const nextOffsetY = event.nativeEvent.layout.y;
        if (ratingSectionOffsetYRef.current === nextOffsetY) {
          return;
        }

        ratingSectionOffsetYRef.current = nextOffsetY;
        setRatingCardLayoutVersion((current) => current + 1);
      }}
    >
      {!hideSummary ? (
        <Column alignItems="center" gap="spacing.general.x2" paddingVertical="spacing.general.x2">
          <Text color="text.secondary" variant="bodyBase">
            {t('reviews.summaryTitle', { ns: 'neighbour-profile' })}
          </Text>
          <Row alignItems="flex-end" gap="spacing.general.x3">
            <Row alignItems="center">
              <Text fontWeight="700" fontSize={36} style={{ color: theme.colors['text.emphasized'] }}>
                👍 {counts.positive}
              </Text>
            </Row>
            <Row alignItems="center">
              <Text fontWeight="600" fontSize={18}>
                👎 {counts.negative}
              </Text>
            </Row>
          </Row>

          <Pressable onPress={() => setIsInfoOpen(true)}>
            <Text color="text.tertiary" style={{ textDecorationLine: 'underline' }} variant="bodyBase">
              {t('reviews.howItWorks', { ns: 'neighbour-profile' })}
            </Text>
          </Pressable>
        </Column>
      ) : null}

      {!hideSummary && topTags.length > 0 && showOverAllTags ? (
        <Column
          gap="spacing.general.x2"
          style={{
            backgroundColor: theme.colors['surface.primary-neutral'],
            borderRadius: theme.borderRadii['radius.lg'],
            padding: theme.spacing['spacing.general.x4'],
          }}
        >
          <Text fontWeight="700" variant="bodyBase">
            {t('reviews.topOpinions', { ns: 'neighbour-profile' })}
          </Text>
          <Row flexWrap="wrap" rowGap="spacing.general.x2" columnGap="spacing.general.x2">
            {topTags.map((tag) => {
              const badge = getBadgeStyle(Math.round(tag.average));
              return (
                <View
                  key={tag.label}
                  style={{
                    backgroundColor: badge.backgroundColor,
                    borderRadius: 1000,
                    paddingHorizontal: theme.spacing['spacing.general.x2'],
                    paddingVertical: theme.spacing['spacing.general.x1'],
                  }}
                >
                  <Text fontWeight="700" style={{ color: theme.colors['text.primary'] }} variant="bodyXSmallMedium">
                    {`${badge.emoji} ${tag.label}`}
                  </Text>
                </View>
              );
            })}
          </Row>
        </Column>
      ) : null}

      {pendingPrompt}

      <Column gap="spacing.general.x4">
        <FlatList
          ref={filtersScrollRef}
          data={ratingFilters}
          horizontal
          keyExtractor={(filter) => filter.key}
          showsHorizontalScrollIndicator={false}
          onScroll={onFiltersScroll}
          scrollEventThrottle={16}
          style={[{ marginHorizontal: -paddingHorizontalInPx }, webScrollStyle as ViewStyle | undefined]}
          contentContainerStyle={{
            gap: theme.spacing['spacing.general.x2'],
            paddingHorizontal: paddingHorizontalInPx,
          }}
          {...(!isMobileNative ? { onMouseDown: onFiltersMouseDown } : {})}
          renderItem={({ item: filter }) => {
            const isActive = activeFilter === filter.key;
            const label =
              filter.key === 'positive'
                ? `👍 (${String(filter.count)})`
                : filter.key === 'negative'
                  ? `👎 (${String(filter.count)})`
                  : t(`reviews.filters.${filter.key}`, { ns: 'neighbour-profile', count: filter.count });
            return (
              <Pressable
                onPress={() => {
                  if (suppressChipPressIfDragged()) {
                    return;
                  }
                  setActiveFilter(filter.key);
                }}
                style={{
                  backgroundColor: isActive ? theme.colors['text.primary'] : theme.colors['surface.primary-neutral'],
                  borderRadius: 100,
                  paddingHorizontal: theme.spacing['spacing.general.x4'],
                  paddingVertical: theme.spacing['spacing.general.x3'],
                }}
              >
                <Text color={isActive ? 'surface.default' : 'text.primary'} variant="bodySmallMedium">
                  {label}
                </Text>
              </Pressable>
            );
          }}
        />

        <Text fontWeight="700" variant="bodyBase">
          {t('reviews.count', { ns: 'neighbour-profile', count: filteredRatings.length })}
        </Text>

        <Column
          marginTop="spacing.general.x2"
          onLayout={(event) => {
            const nextOffsetY = event.nativeEvent.layout.y;
            if (ratingListOffsetYRef.current === nextOffsetY) {
              return;
            }

            ratingListOffsetYRef.current = nextOffsetY;
            setRatingCardLayoutVersion((current) => current + 1);
          }}
        >
          {filteredRatings.map((rating, index) => {
            const ratingKey = getRatingKey(rating, index);

            return (
              <View
                key={ratingKey}
                onLayout={(event) => {
                  if (!rating.loanId) {
                    return;
                  }

                  const nextOffsetY = event.nativeEvent.layout.y;
                  if (ratingCardOffsetYRef.current[rating.loanId] === nextOffsetY) {
                    return;
                  }

                  ratingCardOffsetYRef.current[rating.loanId] = nextOffsetY;
                  setRatingCardLayoutVersion((current) => current + 1);
                }}
              >
                <RatingCard
                  rating={rating}
                  onPressListing={onPressListing}
                  hideListing={hideListing}
                  currentUserId={currentUserId}
                  onEditRating={onEditRating ? () => onEditRating(rating) : undefined}
                  onDeleteRating={() => handleDeleteRating(rating)}
                  onReportRating={() => handleReportRating(rating)}
                />
              </View>
            );
          })}
        </Column>
      </Column>

      <BottomSheet onClose={() => setIsInfoOpen(false)} open={isInfoOpen}>
        <ScrollView>
          <Column
            gap="spacing.general.x4"
            padding="spacing.general.x5"
            style={{ paddingBottom: theme.spacing['spacing.general.x12'] }}
          >
            <Text textAlign="center" variant="headerXSmall">
              {t('reviews.sheet.title', { ns: 'neighbour-profile' })}
            </Text>
            <Text variant="bodyBase">{t('reviews.sheet.paragraph1', { ns: 'neighbour-profile' })}</Text>
            <Text variant="bodyBase">{t('reviews.sheet.paragraph2', { ns: 'neighbour-profile' })}</Text>
            <Text variant="bodyBase">{t('reviews.sheet.paragraph3', { ns: 'neighbour-profile' })}</Text>
          </Column>
        </ScrollView>
      </BottomSheet>
    </Column>
  );
}

export function RatingsSection(props: RatingsSectionProps) {
  return <RatingsSectionView model={useRatingsSectionModel(props)} />;
}
